import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  IconButton,
  Typography,
  Slider,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Crop as CropIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
} from '@mui/icons-material';
import ResponsiveDialogActions from './ResponsiveDialogActions';
import { getInitials } from './avatarEditorHelpers';

interface AvatarEditorProps {
  open: boolean;
  onClose: () => void;
  currentAvatar?: string;
  userName?: string;
  onSave: (base64: string | null) => void;
}

// eslint-disable-next-line max-lines-per-function -- débito pré-existente (F8), corrigir extraindo lógica
const AvatarEditor: React.FC<AvatarEditorProps> = ({
  open,
  onClose,
  currentAvatar,
  userName = '',
  onSave,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(currentAvatar || null);
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const avatarContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openTimeRef = useRef<number>(0);
  const isInitialMountRef = useRef(true);
  const AVATAR_SIZE = 200;

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 2MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImageSrc(result);
      setRotation(0);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      
      // Carregar imagem para poder desenhar
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        // Garantir que o canvas está pronto antes de desenhar
        requestAnimationFrame(() => {
          if (canvasRef.current && imageRef.current) {
            drawCanvas();
          }
        });
      };
      img.onerror = () => {
        setImageSrc(null);
        imageRef.current = null;
      };
      img.src = result;
    };
    reader.readAsDataURL(file);
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = AVATAR_SIZE;
    canvas.height = AVATAR_SIZE;

    const img = imageRef.current;
    const size = AVATAR_SIZE;

    ctx.save();
    ctx.clearRect(0, 0, size, size);
    
    // Criar máscara circular
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();

    // Criar fundo branco
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    // Aplicar transformações
    ctx.translate(size / 2, size / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(scale, scale);
    ctx.translate(-size / 2 + position.x, -size / 2 + position.y);

    // Calcular dimensões para preencher o canvas mantendo proporção
    const imgAspect = img.width / img.height;
    let drawWidth = size;
    let drawHeight = size;

    if (imgAspect > 1) {
      drawHeight = size;
      drawWidth = size * imgAspect;
    } else {
      drawWidth = size;
      drawHeight = size / imgAspect;
    }

    // Desenhar imagem centralizada
    ctx.drawImage(
      img,
      (size - drawWidth) / 2,
      (size - drawHeight) / 2,
      drawWidth,
      drawHeight
    );

    ctx.restore();
  }, [rotation, scale, position]);

  useEffect(() => {
    if (imageSrc && imageRef.current && canvasRef.current) {
      // Usar requestAnimationFrame para garantir que o canvas está pronto
      requestAnimationFrame(() => {
        if (canvasRef.current && imageRef.current) {
          drawCanvas();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageSrc, rotation, scale, position]);

  const handleAvatarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageSrc) {
      e.preventDefault();
      e.stopPropagation();
      // Abrir o file picker imediatamente
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
      return;
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageSrc) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !imageSrc) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;

    setPosition((prev) => ({
      x: prev.x + dx,
      y: prev.y + dy,
    }));
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = (e?: React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsDragging(false);
  };

  const handleRotate = (direction: 'left' | 'right') => {
    setRotation((prev) => prev + (direction === 'left' ? -90 : 90));
  };

  const handleZoom = (_event: Event, newValue: number | number[]) => {
    setScale(newValue as number);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imageSrc) {
      onSave(null);
      onClose();
      return;
    }

    const dataUrl = canvas.toDataURL('image/png');
    onSave(dataUrl);
    onClose();
  };

  const handleRemove = () => {
    setImageSrc(null);
    onSave(null);
    onClose();
  };

  const handleClose = (event?: {}, reason?: string) => {
    // Prevenir fechamento acidental nos primeiros 500ms após abrir o modal
    // Isso evita que cliques acidentais ou eventos propagados fechem o modal imediatamente
    const timeSinceOpen = Date.now() - openTimeRef.current;
    
    // Bloquear fechamento se:
    // 1. Foi aberto há menos de 500ms E
    // 2. É um backdrop click (não é um fechamento intencional)
    if (timeSinceOpen < 500 && reason === 'backdropClick') {
      return;
    }
    
    // Bloquear fechamento se foi aberto há menos de 200ms (qualquer motivo)
    // Isso previne fechamento imediato por qualquer razão
    if (timeSinceOpen < 200) {
      return;
    }
    
    setImageSrc(currentAvatar || null);
    setRotation(0);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    onClose();
  };

  // Resetar quando o diálogo abrir
  useEffect(() => {
    if (open) {
      // Registrar o tempo de abertura para prevenir fechamento acidental
      openTimeRef.current = Date.now();
      isInitialMountRef.current = false;
      
      // Usar currentAvatar apenas se for uma string válida e não vazia
      const validAvatar = currentAvatar && currentAvatar.trim() !== '' ? currentAvatar : null;
      setImageSrc(validAvatar);
      setRotation(0);
      setScale(1);
      setPosition({ x: 0, y: 0 });
      
      if (validAvatar) {
        // Pequeno delay para garantir que o Dialog e canvas estejam totalmente renderizados
        const loadImage = () => {
          const img = new Image();
          img.crossOrigin = 'anonymous'; // Permitir CORS se necessário
          img.onload = () => {
            imageRef.current = img;
            // Forçar atualização do canvas após carregar a imagem
            requestAnimationFrame(() => {
              if (canvasRef.current && imageRef.current) {
                drawCanvas();
              }
            });
          };
          img.onerror = () => {
            setImageSrc(null);
            imageRef.current = null;
          };
          img.src = validAvatar;
        };
        
        // Dar tempo para o Dialog renderizar completamente
        setTimeout(loadImage, 100);
      } else {
        imageRef.current = null;
      }
    } else {
      // Limpar quando o diálogo fechar
      imageRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentAvatar]);

  // Prevenir que eventos de clique no conteúdo do Dialog fechem o modal
  const handleDialogContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        onClick: (e: React.MouseEvent) => {
          // Prevenir que cliques no Paper (conteúdo do modal) fechem o modal
          e.stopPropagation();
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Editar Avatar</Typography>
      </DialogTitle>
      <DialogContent onClick={handleDialogContentClick}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 3 }}>
          {/* Avatar interativo para posicionamento */}
          <Box sx={{ position: 'relative' }}>
            <Box
              ref={avatarContainerRef}
              sx={{
                position: 'relative',
                width: AVATAR_SIZE,
                height: AVATAR_SIZE,
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid #e0e0e0',
                backgroundColor: imageSrc ? 'transparent' : '#f5f5f5',
                cursor: imageSrc ? (isDragging ? 'grabbing' : 'grab') : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={handleAvatarClick}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {imageSrc ? (
                <canvas
                  ref={canvasRef}
                  width={AVATAR_SIZE}
                  height={AVATAR_SIZE}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    color: '#666',
                    fontWeight: 'bold',
                  }}
                >
                  {userName ? getInitials(userName) : 'U'}
                </Typography>
              )}
              {/* Input file oculto para quando clicar no avatar */}
              {!imageSrc && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    e.stopPropagation();
                    const files = e.target.files;
                    if (files && files.length > 0 && files[0]) {
                      handleFileSelect(files[0]);
                    }
                    // Limpar o input DEPOIS de processar o arquivo
                    setTimeout(() => {
                      if (e.target) {
                        (e.target as HTMLInputElement).value = '';
                      }
                    }, 100);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              )}
            </Box>
            
            {imageSrc && (
              <IconButton
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove();
                }}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'error.dark',
                  },
                }}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          {/* Botão para selecionar imagem */}
          {!imageSrc && (
            <Button
              variant="contained"
              component="label"
              startIcon={<CropIcon />}
              onClick={(e) => {
                // Não prevenir o comportamento padrão aqui, deixar o label funcionar
                e.stopPropagation();
              }}
            >
              Selecionar Imagem
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  e.stopPropagation();
                  const files = e.target.files;
                  if (files && files.length > 0 && files[0]) {
                    handleFileSelect(files[0]);
                  }
                  // Limpar o input DEPOIS de processar o arquivo
                  setTimeout(() => {
                    if (e.target) {
                      (e.target as HTMLInputElement).value = '';
                    }
                  }, 100);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </Button>
          )}

          {/* Controles de edição */}
          {imageSrc && (
            <>
              <Box sx={{ width: '100%', px: 2 }}>
                <Typography gutterBottom>Rotacionar</Typography>
                <Box display="flex" gap={1} mb={2}>
                  <Button
                    variant="outlined"
                    startIcon={<RotateLeftIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRotate('left');
                    }}
                    size="small"
                  >
                    Esquerda
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RotateRightIcon />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleRotate('right');
                    }}
                    size="small"
                  >
                    Direita
                  </Button>
                </Box>

                <Typography gutterBottom>Zoom</Typography>
                <Slider
                  value={scale}
                  onChange={handleZoom}
                  min={0.5}
                  max={5}
                  step={0.1}
                  marks={[
                    { value: 0.5, label: '0.5x' },
                    { value: 1, label: '1x' },
                    { value: 2, label: '2x' },
                    { value: 3, label: '3x' },
                    { value: 4, label: '4x' },
                    { value: 5, label: '5x' },
                  ]}
                />

                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                  💡 Arraste a imagem diretamente no avatar para ajustar a posição
                </Typography>
              </Box>

              {/* Botão para trocar imagem */}
              <Button
                variant="outlined"
                component="label"
                startIcon={<CropIcon />}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                Trocar Imagem
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    e.stopPropagation();
                    const files = e.target.files;
                    if (files && files.length > 0 && files[0]) {
                      handleFileSelect(files[0]);
                    }
                    // Limpar o input DEPOIS de processar o arquivo
                    setTimeout(() => {
                      if (e.target) {
                        (e.target as HTMLInputElement).value = '';
                      }
                    }, 100);
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              </Button>
            </>
          )}
        </Box>
      </DialogContent>
      <ResponsiveDialogActions
        secondaryActions={[
          {
            label: 'Cancelar',
            onClick: () => {
              handleClose();
            },
            buttonProps: {
              variant: 'outlined',
              color: 'inherit',
            },
          },
          ...(imageSrc
            ? [
                {
                  label: 'Remover',
                  onClick: () => {
                    handleRemove();
                  },
                  buttonProps: {
                    color: 'error' as const,
                    variant: 'outlined' as const,
                  },
                },
              ]
            : []),
        ]}
        primaryActions={[
          {
            label: 'Salvar',
            onClick: () => {
              handleSave();
            },
            buttonProps: {
              variant: 'contained',
            },
          },
        ]}
      />
    </Dialog>
  );
};

export default AvatarEditor;
